import { requestClassification } from "./aiClient.ts";

export interface ProductClassificationResult {
  category: string;
  confidence: number;
  description?: string;
}

/**
 * Classifies a product based on its name and description using the fast model
 * This is useful for automatically categorizing products or suggesting product groups
 */
export async function classifyProduct(
  productName: string,
  description?: string
): Promise<ProductClassificationResult> {
  const prompt = `Classify the following product into a grocery category:

Product: ${productName}
${description ? `Description: ${description}` : ''}

Please respond with a JSON object in this format:
{
  "category": "the most appropriate grocery category",
  "confidence": 0.95,
  "description": "brief explanation of the classification"
}

Categories should be specific grocery categories like: Dairy, Meat, Produce, Pantry, Beverages, Snacks, Frozen Foods, etc.`;

  try {
    const response = await requestClassification(prompt);
    
    // Try to parse the JSON response
    const cleaned = response.replace(/```json\s*|\s*```/g, '').trim();
    const parsed = JSON.parse(cleaned);
    
    return {
      category: parsed.category || 'Unknown',
      confidence: parsed.confidence || 0.5,
      description: parsed.description
    };
  } catch (error) {
    console.error('Product classification failed:', error);
    return {
      category: 'Unknown',
      confidence: 0,
      description: 'Classification failed'
    };
  }
}

/**
 * Suggests a product group based on the product name
 * This can help users organize their products better
 */
export async function suggestProductGroup(productName: string, existingGroups: string[]): Promise<string> {
  if (existingGroups.length === 0) {
    return 'Misc';
  }

  const prompt = `Suggest the best matching product group from the following list for this product, Respond with just the exact group name from the list, nothing else.
  ${JSON.stringify(
    {
      productName,
      existingGroups,
    }
  )}
`;

  try {
    const response = await requestClassification(prompt);
    const suggested = response.trim();

    if (existingGroups.includes(suggested)) {
      return suggested;
    } else {
      // Fallback to 'Misc' if exists, else first group
      if (existingGroups.includes('Misc')) {
        return 'Misc';
      }
      return existingGroups[0] || '';
    }
  } catch (error) {
    console.error('Product group suggestion failed:', error);
    return existingGroups.includes('Misc') ? 'Misc' : (existingGroups[0] || '');
  }
}

/**
 * Determines if a product is perishable based on its name
 * Useful for setting default expiry dates
 */
export async function classifyPerishability(productName: string): Promise<{
  isPerishable: boolean;
  suggestedDays: number;
  reason: string;
}> {
  const prompt = `Determine if this product is perishable and suggest a default best-before period:

Product: ${productName}

Respond with JSON:
{
  "isPerishable": true/false,
  "suggestedDays": number of days,
  "reason": "brief explanation"
}`;

  try {
    const response = await requestClassification(prompt);
    const cleaned = response.replace(/```json\s*|\s*```/g, '').trim();
    const parsed = JSON.parse(cleaned);
    
    return {
      isPerishable: parsed.isPerishable || false,
      suggestedDays: parsed.suggestedDays || 7,
      reason: parsed.reason || 'Unknown'
    };
  } catch (error) {
    console.error('Perishability classification failed:', error);
    return {
      isPerishable: false,
      suggestedDays: 7,
      reason: 'Classification failed'
    };
  }
}
